import { type FC, type PropsWithChildren, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import {
  useGetArenaOffersQuery,
  useGetShopInfoQuery,
} from '@/api/queries/shop';
import { useGetVipInfoQuery } from '@/api/queries/user';
import { CoinMarket } from '@/components/CoinMarket/CoinMarket';
import BoxMarket from '@/components/OffersMarket/BoxMarket/BoxMarket';
import { OffersMarket } from '@/components/OffersMarket/OffersMarket';
import { Background } from '@/components/Shop/Background/Background';
import { DiceMarket } from '@/components/Shop/DiceMarket/DiceMarket';
import { PromoCodeTab } from '@/components/Shop/PromoCodeTab/PromoCodeTab';
import { useRewardTrackClaimable } from '@/components/Shop/RewardTrackBanner/useRewardTrack';
import {
  type ShopTabId,
  toShopTabId,
} from '@/components/Shop/ShopTabsNav/config';
import { ShopTabsNav } from '@/components/Shop/ShopTabsNav/ShopTabsNav';
import { VipShop } from '@/components/Shop/VipShop/VipShop';
import { Tab } from '@/components/Tabs/Tab';
import { Tabs } from '@/components/Tabs/Tabs';
import { selectDailyTgemBonus } from '@/redux/selectors/userSelectors';
import { useAppSelector } from '@/redux/store';

import styles from './styles.module.scss';

const ShopTab: FC<
  PropsWithChildren<{ tabId: ShopTabId; className?: string }>
> = ({ tabId, className, children }) => (
  <Tab
    className={classNames(
      'flex overflow-y-auto grow',
      styles['scroll-hidden'],
      className,
    )}
    tabId={tabId}
  >
    {children}
  </Tab>
);

const Shop: FC = () => {
  const location = useLocation();
  useGetVipInfoQuery();
  useGetShopInfoQuery();
  useGetArenaOffersQuery();

  const dailyTgemBonus = useAppSelector(selectDailyTgemBonus);
  const freeDiceAvailable = useAppSelector(
    (state) => !!state.user.extendedInfo?.dicePackages?.some((pkg) => pkg.free),
  );
  const freePackAvailable = useAppSelector(
    (state) =>
      !!state.shop.shopPacks?.some(
        (pack) => pack.name === 'free' && pack.nextUseInSeconds <= 0,
      ),
  );
  const rewardTrackClaimable = useRewardTrackClaimable();

  const state = location.state as { activeTab?: string } | undefined;

  useEffect(() => {
    sessionStorage.setItem('arenaShopCheck', 'true');
  }, []);

  return (
    <main className="w-full h-full relative">
      <Background />
      <div className="w-full h-full flex flex-col pt-[calc(var(--tg-safe-area-inset-top,_0px)_+_6.5rem)] isolate">
        <Tabs initActiveTab={toShopTabId(state?.activeTab)}>
          <div className="shrink-0 pb-2">
            <ShopTabsNav
              markers={{
                offers: rewardTrackClaimable,
                tgem: !!dailyTgemBonus?.free || !!dailyTgemBonus?.advertising,
                packs: freePackAvailable,
                dice: freeDiceAvailable,
              }}
            />
          </div>
          <ShopTab tabId="offers">
            <OffersMarket />
          </ShopTab>
          <ShopTab tabId="tgem">
            <CoinMarket />
          </ShopTab>
          <ShopTab tabId="vip">
            <VipShop />
          </ShopTab>
          <ShopTab tabId="packs">
            <BoxMarket />
          </ShopTab>
          <ShopTab tabId="dice">
            <DiceMarket />
          </ShopTab>
          <ShopTab tabId="promo">
            <PromoCodeTab />
          </ShopTab>
        </Tabs>
      </div>
    </main>
  );
};

export { Shop };
